import { Clock, AlertCircle, CheckCircle, XCircle } from 'lucide-react'

export type TicketStatus =
  | 'open'
  | 'in-progress'
  | 'waiting-on-builder'
  | 'resolved'
  | 'closed'
export type TicketPriority = 'low' | 'medium' | 'high' | 'urgent'
export type TicketCategory =
  | 'bug-report'
  | 'feature-request'
  | 'account-issue'
  | 'options-catalog'
  | 'general'
  | ''

export type Message = {
  id: string
  senderId: string
  senderName: string
  senderType: 'builder' | 'admin'
  senderInitials: string
  content: string
  timestamp: string
  attachments?: { name: string; type: string }[]
  isInternal?: boolean
}

export type SupportTicket = {
  id: string
  title: string
  description: string
  status: TicketStatus
  priority: TicketPriority
  category: TicketCategory
  builderId: string
  builderName: string
  assignedTo?: string
  createdDate: string
  updatedDate: string
  messages: Message[]
  unreadByBuilder: boolean
  unreadByAdmin: boolean
}

export const statusConfig = {
  open: {
    label: 'Open',
    variant: 'warning' as const,
    icon: Clock,
    color: 'text-amber-600',
  },
  'in-progress': {
    label: 'In Progress',
    variant: 'primary' as const,
    icon: AlertCircle,
    color: 'text-blue-600',
  },
  'waiting-on-builder': {
    label: 'Waiting on Builder',
    variant: 'neutral' as const,
    icon: Clock,
    color: 'text-slate-600',
  },
  resolved: {
    label: 'Resolved',
    variant: 'success' as const,
    icon: CheckCircle,
    color: 'text-green-600',
  },
  closed: {
    label: 'Closed',
    variant: 'neutral' as const,
    icon: XCircle,
    color: 'text-slate-600',
  },
}

export const priorityConfig = {
  low: { label: 'Low', variant: 'neutral' as const, color: 'text-slate-600' },
  medium: {
    label: 'Medium',
    variant: 'primary' as const,
    color: 'text-blue-600',
  },
  high: { label: 'High', variant: 'warning' as const, color: 'text-amber-600' },
  urgent: {
    label: 'Urgent',
    variant: 'danger' as const,
    color: 'text-red-600',
  },
}

export const categoryConfig = {
  'bug-report': 'Bug Report',
  'feature-request': 'Feature Request',
  'account-issue': 'Account Issue',
  'options-catalog': 'Options & Catalog',
  general: 'General Question',
}

export const adminTeam = [
  'Evan Shoemaker',
  'Sarah Chen',
  'Mike Rodriguez',
  'Unassigned',
]

export const mockTickets: SupportTicket[] = [
  {
    id: 'REQ-001376',
    title: 'Missing textures on Bellevue model',
    description:
      'When viewing the Bellevue model in the 3D viewer, the exterior brick textures are appearing as solid gray boxes. This happens on both desktop and mobile.',
    status: 'in-progress',
    priority: 'high',
    category: 'bug-report',
    builderId: 'BLD-001',
    builderName: 'Mattamy Homes',
    assignedTo: 'Evan Shoemaker',
    createdDate: '2026-03-10T14:30:00Z',
    updatedDate: '2026-03-11T09:15:00Z',
    unreadByBuilder: true,
    unreadByAdmin: false,
    messages: [
      {
        id: 'msg-1',
        senderId: 'b-1',
        senderName: 'Jane Smith',
        senderType: 'builder',
        senderInitials: 'JS',
        content:
          'When viewing the Bellevue model in the 3D viewer, the exterior brick textures are appearing as solid gray boxes. This happens on both desktop and mobile.',
        timestamp: '2026-03-10T14:30:00Z',
        attachments: [{ name: 'screenshot-bug.png', type: 'image/png' }],
      },
      {
        id: 'msg-2',
        senderId: 'a-1',
        senderName: 'Evan Shoemaker',
        senderType: 'admin',
        senderInitials: 'ES',
        content:
          'Thanks for reporting this Jane. I can reproduce the issue on our end. It looks like the material references got unlinked during the last catalog update. I am having the 3D team re-bake the materials now.',
        timestamp: '2026-03-10T15:45:00Z',
      },
      {
        id: 'msg-3',
        senderId: 'a-1',
        senderName: 'Evan Shoemaker',
        senderType: 'admin',
        senderInitials: 'ES',
        content: 'Checking with the 3D team on ETA. Ticket #4922 in Jira.',
        timestamp: '2026-03-10T15:46:00Z',
        isInternal: true,
      },
      {
        id: 'msg-4',
        senderId: 'a-1',
        senderName: 'Evan Shoemaker',
        senderType: 'admin',
        senderInitials: 'ES',
        content:
          'Update: The team has fixed the materials. We are running a new build now and it should be live in about an hour. I will let you know once you can verify.',
        timestamp: '2026-03-11T09:15:00Z',
      },
    ],
  },
  {
    id: 'REQ-001374',
    title: 'Add new appliance package',
    description:
      'We need to add the new Samsung Chef Collection appliance package to all models in the Oakwood Estates project.',
    status: 'waiting-on-builder',
    priority: 'medium',
    category: 'options-catalog',
    builderId: 'BLD-001',
    builderName: 'Mattamy Homes',
    assignedTo: 'Sarah Chen',
    createdDate: '2026-03-08T10:00:00Z',
    updatedDate: '2026-03-09T11:20:00Z',
    unreadByBuilder: false,
    unreadByAdmin: false,
    messages: [
      {
        id: 'msg-1',
        senderId: 'b-1',
        senderName: 'Jane Smith',
        senderType: 'builder',
        senderInitials: 'JS',
        content:
          'We need to add the new Samsung Chef Collection appliance package to all models in the Oakwood Estates project. I have attached the spec sheets and pricing.',
        timestamp: '2026-03-08T10:00:00Z',
        attachments: [
          { name: 'samsung-specs.pdf', type: 'application/pdf' },
          {
            name: 'pricing-q2.xlsx',
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          },
        ],
      },
      {
        id: 'msg-2',
        senderId: 'a-2',
        senderName: 'Sarah Chen',
        senderType: 'admin',
        senderInitials: 'SC',
        content:
          'Hi Jane, I have received the files. Before we proceed, could you confirm if this package should replace the existing LG Studio package, or be offered alongside it?',
        timestamp: '2026-03-09T11:20:00Z',
      },
    ],
  },
  {
    id: 'REQ-001370',
    title: 'Cannot access Analytics tab',
    description:
      'One of our sales reps is getting a 403 error when trying to view the Analytics tab.',
    status: 'resolved',
    priority: 'high',
    category: 'account-issue',
    builderId: 'BLD-002',
    builderName: 'Brookfield Residential',
    assignedTo: 'Mike Rodriguez',
    createdDate: '2026-03-05T09:00:00Z',
    updatedDate: '2026-03-05T14:00:00Z',
    unreadByBuilder: false,
    unreadByAdmin: false,
    messages: [
      {
        id: 'msg-1',
        senderId: 'b-2',
        senderName: 'Mark Johnson',
        senderType: 'builder',
        senderInitials: 'MJ',
        content:
          'One of our sales reps (tom@brookfield.com) is getting a 403 error when trying to view the Analytics tab. He needs this for his weekly report.',
        timestamp: '2026-03-05T09:00:00Z',
      },
      {
        id: 'msg-2',
        senderId: 'a-3',
        senderName: 'Mike Rodriguez',
        senderType: 'admin',
        senderInitials: 'MR',
        content:
          'Hi Mark, I checked Tom\'s account and he was assigned the "Sales Agent" role which doesn\'t have analytics access by default. I have updated his role to "Sales Manager". He should be able to access it now after refreshing.',
        timestamp: '2026-03-05T10:30:00Z',
      },
      {
        id: 'msg-3',
        senderId: 'b-2',
        senderName: 'Mark Johnson',
        senderType: 'builder',
        senderInitials: 'MJ',
        content: 'That fixed it, thanks Mike!',
        timestamp: '2026-03-05T14:00:00Z',
      },
    ],
  },
  {
    id: 'REQ-001380',
    title: 'Feature Request: Export to PDF',
    description:
      'It would be great if buyers could export their selections to a PDF directly from the viewer.',
    status: 'open',
    priority: 'low',
    category: 'feature-request',
    builderId: 'BLD-001',
    builderName: 'Mattamy Homes',
    assignedTo: 'Unassigned',
    createdDate: '2026-03-11T08:00:00Z',
    updatedDate: '2026-03-11T08:00:00Z',
    unreadByBuilder: false,
    unreadByAdmin: true,
    messages: [
      {
        id: 'msg-1',
        senderId: 'b-1',
        senderName: 'Jane Smith',
        senderType: 'builder',
        senderInitials: 'JS',
        content:
          'It would be great if buyers could export their selections to a PDF directly from the viewer. Right now they have to wait for the sales rep to generate it.',
        timestamp: '2026-03-11T08:00:00Z',
      },
    ],
  },
  {
    id: 'REQ-001382',
    title: 'Pricing update for Q3',
    description:
      'Attached is the new pricing matrix for Q3. Please update all models.',
    status: 'open',
    priority: 'urgent',
    category: 'options-catalog',
    builderId: 'BLD-003',
    builderName: 'Pulte Homes',
    assignedTo: 'Unassigned',
    createdDate: '2026-03-11T10:30:00Z',
    updatedDate: '2026-03-11T10:30:00Z',
    unreadByBuilder: false,
    unreadByAdmin: true,
    messages: [
      {
        id: 'msg-1',
        senderId: 'b-3',
        senderName: 'Robert Davis',
        senderType: 'builder',
        senderInitials: 'RD',
        content:
          'Attached is the new pricing matrix for Q3. Please update all models before Monday.',
        timestamp: '2026-03-11T10:30:00Z',
        attachments: [
          {
            name: 'Q3-Pricing-Final.xlsx',
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          },
        ],
      },
    ],
  },
]
